import React, {useEffect, useState} from 'react'
import {
  getConfigurationByName,
  updateConfigurationByConfigID,
} from '../../../../../../API/api-endpoint'
import ToastUtils, {ErrorToastUtils} from '../../../../../../utils/ToastUtils'
import {request} from '../../../../../../middleware/auth-intercepter'
import {API_TYPE} from '../../../../../../utils/Utils'

const DEFAULT_CONFIG = {
  isEnabled: false,
  isEnabledIos: false,
  app_id: '',
  secret_key: '',
  isTestMode: false,
  taxPercentage: 18,
  secure_id_client_id: '',
  secure_id_client_secret: '',
}

const CashfreeUPIPaymentPlugin = () => {
  const [configID, setConfigId] = useState<number | null>(null)
  const [cashfreeConfig, setCashfreeConfig] = useState<any>(DEFAULT_CONFIG)
  const [isNewConfig, setIsNewConfig] = useState(false)

  useEffect(() => {
    getConfiguration()
  }, [])

  const handleChange = (event: any) => {
    const name = event.target.name
    const isToggle = name === 'isEnabled' || name === 'isEnabledIos' || name === 'isTestMode'

    if (isToggle) {
      const updated = {...cashfreeConfig, [name]: event.target.checked}
      setCashfreeConfig(updated)
      if (!isNewConfig) {
        updateConfiguration(updated)
      }
    } else if (name === 'taxPercentage') {
      let rawVal = event.target.value
      if (rawVal === '') {
        setCashfreeConfig({...cashfreeConfig, [name]: ''})
        return
      }
      let val = parseFloat(rawVal)
      if (isNaN(val)) return
      if (val < 0) val = 0
      if (val > 100) val = 100
      setCashfreeConfig({...cashfreeConfig, [name]: val})
    } else {
      setCashfreeConfig({...cashfreeConfig, [name]: event.target.value})
    }
  }

  const onBlurUpdate = () => {
    if (!isNewConfig) {
      let finalConfig = {...cashfreeConfig}
      if (finalConfig.taxPercentage === '' || isNaN(parseFloat(finalConfig.taxPercentage))) {
        finalConfig.taxPercentage = 18
        setCashfreeConfig(finalConfig)
      }
      updateConfiguration(finalConfig)
    }
  }

  const getConfiguration = async () => {
    const result = await getConfigurationByName('cashfreeupi')
    if (result?.success && result?.data) {
      const parsedData = JSON.parse(result.data.values)
      setConfigId(result.data.id)
      setIsNewConfig(false)
      setCashfreeConfig({
        isEnabled: parsedData?.isEnabled ?? false,
        isEnabledIos: parsedData?.isEnabledIos ?? false,
        app_id: parsedData?.app_id ?? '',
        secret_key: parsedData?.secret_key ?? '',
        isTestMode: parsedData?.isTestMode ?? false,
        taxPercentage: parsedData?.taxPercentage ?? 18,
        payout_client_id: parsedData?.payout_client_id ?? '',
        payout_client_secret: parsedData?.payout_client_secret ?? '',
        secure_id_client_id: parsedData?.secure_id_client_id ?? '',
        secure_id_client_secret: parsedData?.secure_id_client_secret ?? '',
      })
    } else {
      // No config row in DB yet — show defaults, let user create it
      setIsNewConfig(true)
      setCashfreeConfig(DEFAULT_CONFIG)
    }
  }

  const createConfiguration = async () => {
    try {
      let finalConfig = {...cashfreeConfig}
      if (finalConfig.taxPercentage === '' || isNaN(parseFloat(finalConfig.taxPercentage))) {
        finalConfig.taxPercentage = 18
      }
      const response = await request(
        '/masters/configurations',
        API_TYPE.POST,
        {
          type: 'cashfreeupi',
          category: 'payment',
          status: true,
          values: JSON.stringify(finalConfig),
        },
        true,
        false
      )
      if (response?.success) {
        ToastUtils({type: 'success', message: 'Cashfree UPI Configuration Created Successfully'})
        getConfiguration()
      } else {
        ErrorToastUtils()
      }
    } catch (e) {
      ErrorToastUtils()
    }
  }

  const updateConfiguration = async (config: any) => {
    if (configID === null) return
    const result = await updateConfigurationByConfigID(configID, config, null)
    if (result.success) {
      getConfiguration()
      ToastUtils({type: 'success', message: 'Cashfree UPI Configuration Saved Successfully'})
    } else {
      ErrorToastUtils()
    }
  }

  return (
    <div className='card'>
      <div className='card-title p-8'>
        <h2>Cashfree UPI Payment Settings</h2>
        {isNewConfig && (
          <div className='alert alert-warning mt-3'>
            <strong>No Cashfree UPI configuration found in database.</strong> Fill in your credentials
            below and click <strong>Save Configuration</strong> to create it.
          </div>
        )}
      </div>
      <div className='card-body'>
        <div className='row no-gutters'>

          {/* Enable / Disable */}
          <div className='col-lg-4 card-body bg-light'>
            <p>
              <strong className='headings-color'>Enable Cashfree UPI Plugin</strong>
            </p>
            <p className='text-muted'>Enable or disable the Cashfree UPI payment gateway</p>
          </div>
          <div className='col-lg-8 card-form__body card-body d-flex align-items-center bg-white'>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                role='switch'
                id='cashfreeUpiIsEnabled'
                name='isEnabled'
                checked={cashfreeConfig.isEnabled}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Enable / Disable for iOS */}
          <div className='col-lg-4 card-body bg-light'>
            <p>
              <strong className='headings-color'>Enable Cashfree UPI Plugin for iOS</strong>
            </p>
            <p className='text-muted'>Enable or disable the Cashfree UPI payment gateway for iOS</p>
          </div>
          <div className='col-lg-8 card-form__body card-body d-flex align-items-center bg-white'>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                role='switch'
                id='cashfreeUpiIsEnabledIos'
                name='isEnabledIos'
                checked={cashfreeConfig.isEnabledIos}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* App ID */}
          <div className='col-lg-4 card-body bg-light'>
            <p>
              <strong className='headings-color'>App ID</strong>
            </p>
            <p className='text-muted'>
              Your Cashfree App ID (<code>x-client-id</code>). Found in the Cashfree merchant
              dashboard under <em>Developers → API Keys</em>.
            </p>
          </div>
          <div className='col-lg-8 card-form__body card-body d-flex align-items-center bg-white'>
            <div className='flex w-100'>
              <input
                type='text'
                className='form-control'
                name='app_id'
                placeholder='e.g. CF1234567890'
                value={cashfreeConfig.app_id}
                onChange={handleChange}
                onBlur={onBlurUpdate}
              />
            </div>
          </div>

          {/* Secret Key */}
          <div className='col-lg-4 card-body bg-light'>
            <p>
              <strong className='headings-color'>Secret Key</strong>
            </p>
            <p className='text-muted'>
              Your Cashfree Secret Key (<code>x-client-secret</code>). Found in the Cashfree
              merchant dashboard.
            </p>
          </div>
          <div className='col-lg-8 card-form__body card-body d-flex align-items-center bg-white'>
            <div className='flex w-100'>
              <input
                type='password'
                className='form-control'
                name='secret_key'
                placeholder='e.g. cfsk_ma_prod_...'
                value={cashfreeConfig.secret_key}
                onChange={handleChange}
                onBlur={onBlurUpdate}
              />
            </div>
          </div>

          {/* Secure ID Client ID */}
          <div className='col-lg-4 card-body bg-light'>
            <p>
              <strong className='headings-color'>Secure ID Client ID</strong>
            </p>
            <p className='text-muted'>
              Your Cashfree Secure ID (Verification Suite) Client ID (<code>x-client-id</code>). Found in the Cashfree verification dashboard under <em>Developers → API Keys</em>.
            </p>
          </div>
          <div className='col-lg-8 card-form__body card-body d-flex align-items-center bg-white'>
            <div className='flex w-100'>
              <input
                type='text'
                className='form-control'
                name='secure_id_client_id'
                placeholder='e.g. CF1234567890'
                value={cashfreeConfig.secure_id_client_id}
                onChange={handleChange}
                onBlur={onBlurUpdate}
              />
            </div>
          </div>

          {/* Secure ID Client Secret */}
          <div className='col-lg-4 card-body bg-light'>
            <p>
              <strong className='headings-color'>Secure ID Client Secret</strong>
            </p>
            <p className='text-muted'>
              Your Cashfree Secure ID (Verification Suite) Client Secret (<code>x-client-secret</code>). Found in the Cashfree verification dashboard under <em>Developers → API Keys</em>.
            </p>
          </div>
          <div className='col-lg-8 card-form__body card-body d-flex align-items-center bg-white'>
            <div className='flex w-100'>
              <input
                type='password'
                className='form-control'
                name='secure_id_client_secret'
                placeholder='e.g. cfsk_ma_prod_...'
                value={cashfreeConfig.secure_id_client_secret}
                onChange={handleChange}
                onBlur={onBlurUpdate}
              />
            </div>
          </div>

          {/* Test Mode */}
          <div className='col-lg-4 card-body bg-light'>
            <p>
              <strong className='headings-color'>Enable Test Mode</strong>
            </p>
            <p className='text-muted'>
              Toggle sandbox / live mode. When enabled, payments hit{' '}
              <code>sandbox.cashfree.com</code>. Also update{' '}
              <code>order-pay/config.php</code> on the workway server with the matching
              credentials.
            </p>
          </div>
          <div className='col-lg-8 card-form__body card-body d-flex align-items-center bg-white'>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                role='switch'
                id='cashfreeUpiIsTestMode'
                name='isTestMode'
                checked={cashfreeConfig.isTestMode}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Tax Percentage */}
          <div className='col-lg-4 card-body bg-light'>
            <p>
              <strong className='headings-color'>Tax Percentage (%)</strong>
            </p>
            <p className='text-muted'>Dynamic percentage between 0 and 100</p>
          </div>
          <div className='col-lg-8 card-form__body card-body d-flex align-items-center bg-white'>
            <div className='flex w-100'>
              <input
                type='number'
                min='0'
                max='100'
                step='any'
                className='form-control'
                name='taxPercentage'
                placeholder='e.g. 18'
                value={cashfreeConfig.taxPercentage}
                onChange={handleChange}
                onBlur={onBlurUpdate}
              />
            </div>
          </div>

        </div>

        {/* Create button only shown when no config row exists yet */}
        {isNewConfig && (
          <div className='d-flex justify-content-end mt-4 px-4 pb-4'>
            <button
              className='btn btn-primary'
              onClick={createConfiguration}
            >
              Save Configuration
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default CashfreeUPIPaymentPlugin
